import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchPartners, fetchPartner, updatePartner, fetchNotes, createNote, updateNote, deleteNote,
  fetchUsers, createUser,
  createContactTag, updateContactTag, deleteContactTag,
  fetchSchedules, createSchedule, updateSchedule, deleteSchedule,
  fetchAddresses, createAddress, updateAddress,
  fetchBlackoutDays, createBlackoutDay,
  deletePartner,
} from "api";
import {
  PageHeader, Table, Spinner, EmptyState, FormField, Modal, Select,
} from "components/common";
import { useAuth } from "hooks/useAuth";
import { formatSlotTime, slotToInput, inputToSlot, SCHEDULE_TIME_ZONE_LABEL } from "utils/scheduleTime";
import type { PartnerEntity, ContactUser, ContactTag, ContactTagType, EntityAddress, Schedule, DayOfWeek, Note, BlackoutDay } from "types";

// ─── Partners list ────────────────────────────────────────────────────────────

type StatusFilter = "all" | "active" | "inactive";

function fuzzyMatch(text: string, query: string): boolean {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  // Check substring match first
  if (lower.includes(q)) return true;
  // Simple fuzzy: every character in query appears in order in text
  let ti = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const found = lower.indexOf(q[qi], ti);
    if (found === -1) return false;
    ti = found + 1;
  }
  return true;
}

export function PartnersPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const { data: partners = [], isLoading } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const filtered = partners.filter((p) => {
    if (statusFilter === "active" && !p.active) return false;
    if (statusFilter === "inactive" && p.active) return false;
    if (search.trim() && !fuzzyMatch(p.name, search.trim())) return false;
    return true;
  });

  if (isLoading) return <Spinner className="mt-20" />;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8">
      <PageHeader title="Partners" subtitle={`${filtered.length} of ${partners.length} partners`} />

      {/* Search + filters */}
      <div className="flex items-center gap-3 mb-4">
        <input
          type="text"
          className="input flex-1"
          placeholder="Search partners…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {isAdmin && (
          <div className="flex rounded-lg border border-gray-200 overflow-hidden shrink-0">
            {(["all", "active", "inactive"] as StatusFilter[]).map((value) => (
              <button
                key={value}
                onClick={() => setStatusFilter(value)}
                className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                  statusFilter === value
                    ? "bg-gray-800 text-white"
                    : "bg-white text-gray-500 hover:bg-gray-50"
                }`}
              >
                {value}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <EmptyState message={search || statusFilter !== "all" ? "No partners match your filters." : "No partners yet."} />
        ) : (
          <Table
            data={filtered}
            keyExtractor={(p) => p.id}
            onRowClick={(p) => navigate(`/partners/${p.id}`)}
            columns={[
              { header: "Name", accessor: (p) => p.name },
              { header: "Type", accessor: (p) => (
                <span className={`badge capitalize ${
                  p.type === "donator"     ? "bg-green-100 text-green-700" :
                  p.type === "distributor" ? "bg-indigo-100 text-indigo-700" :
                  p.type === "ops"         ? "bg-orange-100 text-orange-700" :
                  "bg-gray-100 text-gray-500"
                }`}>
                  {p.type}
                </span>
              ) },
              ...(isAdmin ? [{
                header: "Status",
                accessor: (p: PartnerEntity) => (
                  <span className={`badge ${p.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {p.active ? "Active" : "Inactive"}
                  </span>
                ),
              }] : []),
            ]}
          />
        )}
      </div>
    </div>
  );
}

// ─── Partner detail ───────────────────────────────────────────────────────────

const DAYS: DayOfWeek[] = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];

export function PartnerDetailPage() {
  const { id }                 = useParams<{ id: string }>();
  const navigate               = useNavigate();
  const { isAdmin, isCourier } = useAuth();
  const queryClient            = useQueryClient();
  const partnerId              = Number(id);
  const canSeeNotes            = isAdmin || isCourier;

  // ── Data queries ──────────────────────────────────────────────────────────
  const { data: partner, isLoading } = useQuery({
    queryKey: ["partners", partnerId],
    queryFn:  () => fetchPartner(partnerId),
  });

  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts", partnerId],
    queryFn:  () => fetchUsers({ partner_entity_id: String(partnerId) }),
  });

  const { data: contactUsers = [] } = useQuery({
    queryKey: ["users", { partner_entity_id: partnerId }],
    queryFn:  () => fetchUsers({ partner_entity_id: partnerId }),
    enabled:  !!partnerId,
  });


  const { data: addresses = [] } = useQuery({
    queryKey: ["addresses", partnerId],
    queryFn:  () => fetchAddresses({ partner_entity_id: String(partnerId) }),
  });

  const { data: schedules = [] } = useQuery({
    queryKey: ["schedules", partnerId],
    queryFn:  () => fetchSchedules({ partner_entity_id: String(partnerId) }),
  });

  const { data: notes = [] } = useQuery({
    queryKey: ["notes", partnerId],
    queryFn:  () => fetchNotes(partnerId),
    enabled:  canSeeNotes,
  });

  const { data: couriers = [] } = useQuery({
    queryKey: ["users", { role: "bridge13_courier" }],
    queryFn:  () => fetchUsers({ role: "bridge13_courier" }),
    enabled:  isAdmin,
  });

  // ── Preferred courier mutation ─────────────────────────────────────────────
  const { mutate: setPreferredCourier } = useMutation({
    mutationFn: (courierId: number | null) =>
      updatePartner(partnerId, { preferred_courier_id: courierId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners", partnerId] });
    },
  });

  // ── Modal state ───────────────────────────────────────────────────────────
  const [showContactModal,  setShowContactModal]  = useState(false);
  const [showAddressModal,  setShowAddressModal]  = useState(false);
  const [editingAddress, setEditingAddress]        = useState<EntityAddress | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule]     = useState<Schedule | null>(null);
  const [newNote, setNewNote]                     = useState("");
  const [editingNote, setEditingNote]             = useState<Note | null>(null);
  const [deletingNote, setDeletingNote]           = useState<Note | null>(null);
  const [editingName, setEditingName]             = useState(false);
  const [showBlackoutDayModal, setShowBlackoutDayModal] = useState(false);
  const [tagModalUser, setTagModalUser]                 = useState<ContactUser | null>(null);
  const [showRemoveModal, setShowRemoveModal]           = useState(false);

  // ── Blackout days query ───────────────────────────────────────────────────
  const { data: futureBlackoutDays = [] } = useQuery({
    queryKey: ["blackoutDays", partnerId, "future"],
    queryFn:  () => fetchBlackoutDays({ partner_entity_id: partnerId, future_only: true }),
    enabled:  isAdmin,
  });

  // ── Note mutation ─────────────────────────────────────────────────────────
  const { mutate: submitNote, isPending: savingNote } = useMutation({
    mutationFn: () => createNote(partnerId, newNote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notes", partnerId] });
      setNewNote("");
    },
  });

  if (isLoading) return <Spinner className="mt-20" />;
  if (!partner)  return <EmptyState message="Partner not found." />;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 space-y-6 max-w-3xl">
      <button
        onClick={() => navigate("/partners")}
        className="text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
      >
        ← Partners
      </button>

      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-gray-900">{partner.name}</h1>
            {isAdmin && (
              <button
                onClick={() => setEditingName(true)}
                className="text-gray-300 hover:text-gray-500 transition-colors"
                title="Edit partner"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                  <path d="m15 5 4 4" />
                </svg>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`badge capitalize ${
              partner.type === "donator"     ? "bg-green-100 text-green-700" :
              partner.type === "distributor" ? "bg-indigo-100 text-indigo-700" :
              partner.type === "ops"         ? "bg-orange-100 text-orange-700" :
              "bg-gray-100 text-gray-500"
            }`}>
              {partner.type}
            </span>
            <span className={`badge ${partner.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
              {partner.active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </div>

      {/* ── Actions bar ────────────────────────────────────────────────────── */}
      {isAdmin && (
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => navigate(`/logs/new?partner_entity_id=${partnerId}`)}
            className="card px-4 py-4 flex flex-col items-center gap-2 hover:bg-gray-50 transition-colors text-center group"
          >
            <span className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center group-hover:bg-green-200 transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
            </span>
            <span className="text-xs font-medium text-gray-700">Create Food Log</span>
          </button>

          <button
            onClick={() => navigate(`/logs?partner_entity_id=${partnerId}`)}
            className="card px-4 py-4 flex flex-col items-center gap-2 hover:bg-gray-50 transition-colors text-center group"
          >
            <span className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                <path d="M14 2v6h6" />
                <path d="M16 13H8" />
                <path d="M16 17H8" />
                <path d="M10 9H8" />
              </svg>
            </span>
            <span className="text-xs font-medium text-gray-700">View Food Logs</span>
          </button>

          <button
            onClick={() => {/* TODO: Generate Report */}}
            className="card px-4 py-4 flex flex-col items-center gap-2 hover:bg-gray-50 transition-colors text-center group"
          >
            <span className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v18h18" />
                <path d="m19 9-5 5-4-4-3 3" />
              </svg>
            </span>
            <span className="text-xs font-medium text-gray-700">Generate Report</span>
          </button>
        </div>
      )}

      {/* ── Addresses ──────────────────────────────────────────────────────── */}
      <Section
        title="Addresses"
        action={isAdmin && (
          addresses.length > 0 ? (
            <button className="btn-secondary text-xs py-1" onClick={() => {
              setEditingAddress(addresses[0]);
              setShowAddressModal(true);
            }}>
              Edit address
            </button>
          ) : (
            <button className="btn-secondary text-xs py-1" onClick={() => {
              setEditingAddress(null);
              setShowAddressModal(true);
            }}>
              Add address
            </button>
          )
        )}
      >
        {addresses.length === 0 ? (
          <p className="text-sm text-gray-400">No addresses yet.</p>
        ) : (
          <div className="space-y-3">
            {addresses.map((a) => (
              <div key={a.id} className="bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-700">
                <p>{a.address}{a.address_2 ? `, ${a.address_2}` : ""}</p>
                <p className="text-gray-500">{a.city}, {a.state} {a.zip}</p>
                {a.coordinates && a.coordinates.lat && a.coordinates.lng && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-gray-200">
                    <iframe
                      title={`Map of ${a.address}`}
                      width="100%"
                      height="200"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${a.coordinates.lng - 0.005},${a.coordinates.lat - 0.003},${a.coordinates.lng + 0.005},${a.coordinates.lat + 0.003}&layer=mapnik&marker=${a.coordinates.lat},${a.coordinates.lng}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── Contact users ──────────────────────────────────────────────────── */}
      <Section
        title="Contact users"
        action={isAdmin && (
          <button className="btn-secondary text-xs py-1" onClick={() => navigate(`/users/new?partner_entity_id=${partnerId}`)}>
            Add contact
          </button>
        )}
      >
        {contactUsers.length === 0 ? (
          <EmptyState message="No contact users for this partner." />
        ) : (
          <Table
            data={contactUsers}
            keyExtractor={(u) => u.id}
            onRowClick={(u) => navigate(`/users/${u.id}`)}
            columns={[
              {
                header: "Name",
                accessor: (u: ContactUser) => {
                  const name = `${u.name_1}${u.name_2 ? ` ${u.name_2}` : ""}`;
                  const partnerTags = (u.contact_tags ?? []).filter(
                    (ct) => ct.partner_entity_id === partnerId
                  );
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{name}</span>
                      {partnerTags.map((ct) => (
                        <span
                          key={ct.id}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                            ct.tag === "primary"   ? "bg-blue-100 text-blue-700"   :
                            ct.tag === "secondary" ? "bg-purple-100 text-purple-700" :
                            "bg-red-100 text-red-700"
                          }`}
                        >
                          {ct.tag}
                        </span>
                      ))}
                    </div>
                  );
                },
              },
              {
                header: "Email",
                accessor: (u: ContactUser) => u.email
                  ? <a href={`mailto:${u.email}`} className="text-indigo-600 hover:underline" onClick={(e) => e.stopPropagation()}>{u.email}</a>
                  : "—",
              },
              { header: "Title", accessor: (u: ContactUser) => u.title ?? "—" },
              {
                header: "Phone",
                accessor: (u: ContactUser) => u.phone
                  ? <a href={`tel:${u.phone}`} className="text-indigo-600 hover:underline" onClick={(e) => e.stopPropagation()}>{u.phone}</a>
                  : "—",
              },
              ...(isAdmin ? [{
                header: "Role Management",
                accessor: (u: ContactUser) => {
                  const hasTag = (u.contact_tags ?? []).some(
                    (ct) => ct.partner_entity_id === partnerId
                  );
                  return (
                    <button
                      className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                      onClick={(e) => { e.stopPropagation(); setTagModalUser(u); }}
                    >
                      {hasTag ? "edit role" : "add role"}
                    </button>
                  );
                },
              }] : []),
            ]}
          />
        )}
      </Section>

      {/* ── Preferred courier ──────────────────────────────────────────────── */}
      {isAdmin && (
        <Section title="Preferred courier">
          <Select
            className="max-w-xs"
            value={String(partner.preferred_courier_id ?? "")}
            onChange={(e) => {
              const val = e.target.value;
              setPreferredCourier(val === "" ? null : Number(val));
            }}
          >
            <option value="">— None —</option>
            {couriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name_1}{c.name_2 ? ` ${c.name_2}` : ""}
              </option>
            ))}
          </Select>
        </Section>
      )}

      {/* ── Schedules ──────────────────────────────────────────────────────── */}
      <Section
        title={
          partner.type === "donator"     ? "Pickup schedules" :
          partner.type === "distributor" ? "Delivery schedules" :
          "Schedules"
        }
        action={isAdmin && addresses.length > 0 && (
          schedules.length > 0 ? (
            <button className="btn-secondary text-xs py-1" onClick={() => {
              setEditingSchedule(schedules[0]);
              setShowScheduleModal(true);
            }}>
              Edit schedule
            </button>
          ) : (
            <button className="btn-secondary text-xs py-1" onClick={() => {
              setEditingSchedule(null);
              setShowScheduleModal(true);
            }}>
              Add schedule
            </button>
          )
        )}
      >
        {schedules.length === 0 ? (
          <p className="text-sm text-gray-400">
            {addresses.length === 0
              ? "Add an address first, then create a schedule."
              : "No schedules yet."}
          </p>
        ) : (
          <div className="space-y-4">
            {schedules.map((s) => {
              const addr = addresses.find((a) => a.id === s.entity_address_id);
              return (
                <div key={s.id} className="bg-gray-50 rounded-lg px-4 py-3">
                  {addr && (
                    <p className="text-xs text-gray-500 font-medium mb-2">
                      {addr.address}, {addr.city}
                    </p>
                  )}
                  <div className="space-y-1 text-sm">
                    {DAYS.filter((d) => (s.schedule[d]?.length ?? 0) > 0).map((day) => (
                      <div key={day} className="flex gap-2">
                        <span className="capitalize text-gray-500 w-24 shrink-0">{day}</span>
                        <div className="space-y-0.5">
                          {s.schedule[day]!.map((slot, i) => (
                            <div key={i} className="text-gray-800">
                              {formatSlotTime(slot.start)} – {formatSlotTime(slot.end)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* ── Blackout days ────────────────────────────────────────────────── */}
      {isAdmin && (
        <Section
          title="Blackout days"
          action={
            <div className="flex items-center gap-2">
              <button
                className="btn-secondary text-xs py-1"
                onClick={() => navigate(`/partners/${partnerId}/blackout-days`)}
              >
                View all
              </button>
              <button
                className="btn-secondary text-xs py-1"
                onClick={() => setShowBlackoutDayModal(true)}
              >
                Add new
              </button>
            </div>
          }
        >
          {futureBlackoutDays.length === 0 ? (
            <p className="text-sm text-gray-400">No blackout days yet.</p>
          ) : (
            <Table
              data={futureBlackoutDays}
              keyExtractor={(b: BlackoutDay) => b.id}
              columns={[
                {
                  header:   "Date",
                  accessor: (b: BlackoutDay) => format(parseISO(b.date), "EEEE, MMMM d, yyyy"),
                },
              ]}
            />
          )}
        </Section>
      )}

      {/* ── Notes ──────────────────────────────────────────────────────────── */}
      {canSeeNotes && (
        <Section title="Notes">
          <div className="space-y-2 mb-4">
            {notes.length === 0 && <p className="text-sm text-gray-400">No notes yet.</p>}
            {notes.map((n) => (
              <div key={n.id} className="flex items-start justify-between bg-gray-50 rounded-lg px-3 py-2 group">
                <p className="text-sm text-gray-700 flex-1">{n.note}</p>
                {isAdmin && (
                  <NoteActionsMenu
                    onEdit={() => setEditingNote(n)}
                    onDelete={() => setDeletingNote(n)}
                  />
                )}
              </div>
            ))}
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <input
                className="input flex-1"
                placeholder="Add a note…"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <button
                className="btn-primary"
                disabled={!newNote.trim() || savingNote}
                onClick={() => submitNote()}
              >
                Save
              </button>
            </div>
          )}
        </Section>
      )}

      {/* ── Remove partner ─────────────────────────────────────────────────── */}
      {isAdmin && (
        <Section title="Danger zone">
          <button
            className="btn-primary bg-red-600 hover:bg-red-700"
            onClick={() => setShowRemoveModal(true)}
          >
            Remove Partner
          </button>
        </Section>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {editingNote && (
        <EditNoteModal
          note={editingNote}
          onClose={() => setEditingNote(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["notes", partnerId] });
            setEditingNote(null);
          }}
        />
      )}

      {deletingNote && (
        <DeleteNoteModal
          note={deletingNote}
          onClose={() => setDeletingNote(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["notes", partnerId] });
            setDeletingNote(null);
          }}
        />
      )}

      {editingName && (
        <EditPartnerModal
          partnerId={partnerId}
          currentName={partner.name}
          currentType={partner.type}
          currentActive={partner.active}
          onClose={() => setEditingName(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["partners", partnerId] });
            queryClient.invalidateQueries({ queryKey: ["partners"] });
            setEditingName(false);
          }}
        />
      )}

      {showContactModal && (
        <AddContactModal
          partnerId={partnerId}
          onClose={() => setShowContactModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["contacts", partnerId] });
            setShowContactModal(false);
          }}
        />
      )}

      {showAddressModal && (
        <AddressModal
          partnerId={partnerId}
          existing={editingAddress}
          onClose={() => { setShowAddressModal(false); setEditingAddress(null); }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["addresses", partnerId] });
            setShowAddressModal(false);
            setEditingAddress(null);
          }}
        />
      )}

      {showScheduleModal && (
        <ScheduleModal
          partnerId={partnerId}
          addresses={addresses}
          existing={editingSchedule}
          onClose={() => { setShowScheduleModal(false); setEditingSchedule(null); }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["schedules", partnerId] });
            setShowScheduleModal(false);
            setEditingSchedule(null);
          }}
        />
      )}

      {showBlackoutDayModal && (
        <AddBlackoutDayModal
          partnerId={partnerId}
          existingDates={futureBlackoutDays.map((b) => b.date)}
          onClose={() => setShowBlackoutDayModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["blackoutDays", partnerId, "future"] });
            setShowBlackoutDayModal(false);
          }}
        />
      )}

      {tagModalUser && (
        <ManageTagModal
          user={tagModalUser}
          partnerId={partnerId}
          onClose={() => setTagModalUser(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["users", { partner_entity_id: partnerId }] });
            queryClient.invalidateQueries({ queryKey: ["contacts", partnerId] });
          }}
        />
      )}

      {showRemoveModal && (
        <RemovePartnerModal
          partnerId={partnerId}
          partnerName={partner.name}
          onClose={() => setShowRemoveModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["partners"] });
            navigate("/partners");
          }}
        />
      )}
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({
  title,
  action,
  children,
}: {
  title:     string;
  action?:   React.ReactNode;
  children:  React.ReactNode;
}) {
  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

// ─── Manage Tag Modal ─────────────────────────────────────────────────────────

const TAG_OPTIONS: ContactTagType[] = ["primary", "secondary", "emergency"];

const TAG_STYLES: Record<ContactTagType, string> = {
  primary:   "bg-blue-100 text-blue-700",
  secondary: "bg-purple-100 text-purple-700",
  emergency: "bg-red-100 text-red-700",
};

function ManageTagModal({
  user,
  partnerId,
  onClose,
  onSuccess,
}: {
  user:      ContactUser;
  partnerId: number;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const existing = (user.contact_tags ?? []).find((ct) => ct.partner_entity_id === partnerId) ?? null;

  const [selectedTag, setSelectedTag] = useState<ContactTagType | "">(existing?.tag ?? "");
  const [localTag,    setLocalTag]    = useState<ContactTag | null>(existing);
  const [error,       setError]       = useState<string | null>(null);

  const { mutate: saveTag, isPending: saving } = useMutation({
    mutationFn: () =>
      localTag
        ? updateContactTag(localTag.id, selectedTag as string)
        : createContactTag({ contact_user_id: user.id, partner_entity_id: partnerId, tag: selectedTag as string }),
    onSuccess: (saved) => {
      setLocalTag(saved);
      setError(null);
      onSuccess();
    },
    onError: () => setError("Failed to save role."),
  });

  const { mutate: removeTag, isPending: removing } = useMutation({
    mutationFn: () => deleteContactTag(localTag!.id),
    onSuccess: () => {
      setLocalTag(null);
      setSelectedTag("");
      setError(null);
      onSuccess();
    },
    onError: () => setError("Failed to remove role."),
  });

  const userName    = `${user.name_1}${user.name_2 ? ` ${user.name_2}` : ""}`;
  const isDirty     = selectedTag !== (localTag?.tag ?? "");
  const canSave     = !!selectedTag && isDirty;

  return (
    <Modal title={`Assign role — ${userName}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value as ContactTagType)}
          >
            <option value="">— No role —</option>
            {TAG_OPTIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>

          {canSave && (
            <button
              className="btn-primary text-sm py-1.5 px-3"
              disabled={saving}
              onClick={() => saveTag()}
            >
              {saving ? <Spinner className="h-4" /> : "Save"}
            </button>
          )}

          {localTag && !isDirty && (
            <button
              className="text-sm text-red-500 hover:text-red-700"
              disabled={removing}
              onClick={() => removeTag()}
            >
              {removing ? <Spinner className="h-4" /> : "Remove"}
            </button>
          )}
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex justify-end pt-1">
          <button className="btn-secondary" onClick={onClose}>Done</button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Add Contact Modal ────────────────────────────────────────────────────────

function AddContactModal({
  partnerId,
  onClose,
  onSuccess,
}: {
  partnerId: number;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState({
    name_1: "", name_2: "", email: "", phone: "", title: "",
  });

  const set = (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const { mutate, isPending, error } = useMutation({
    mutationFn: () =>
      createUser({
        partner_entity_id: partnerId,
        name_1:            form.name_1.trim(),
        name_2:            form.name_2.trim() || undefined,
        email:             form.email.trim()  || undefined,
        phone:             form.phone.trim()  || undefined,
        title:             form.title.trim()  || undefined,
      }),
    onSuccess,
  });

  const isValid = form.name_1.trim();

  return (
    <Modal title="Add contact user" onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="First name">
            <input className="input" value={form.name_1} onChange={set("name_1")} placeholder="Jane" autoFocus />
          </FormField>
          <FormField label="Last name">
            <input className="input" value={form.name_2} onChange={set("name_2")} placeholder="Smith" />
          </FormField>
        </div>
        <FormField label="Email">
          <input className="input" type="email" value={form.email} onChange={set("email")} placeholder="jane@example.com" />
        </FormField>
        <FormField label="Phone">
          <input className="input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+1 (555) 000-0000" />
        </FormField>
        <FormField label="Title">
          <input className="input" value={form.title} onChange={set("title")} placeholder="Operations Manager" />
        </FormField>
        {error && <p className="text-sm text-red-500">Something went wrong. Please try again.</p>}
        <div className="flex gap-3 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!isValid || isPending}
            onClick={() => mutate()}
          >
            {isPending ? <Spinner className="h-4" /> : "Create contact"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Address Modal (create + edit) ────────────────────────────────────────────

function AddressModal({
  partnerId,
  existing,
  onClose,
  onSuccess,
}: {
  partnerId: number;
  existing:  EntityAddress | null;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const isEditing = !!existing;

  const [form, setForm] = useState({
    address:   existing?.address   ?? "",
    address_2: existing?.address_2 ?? "",
    city:      existing?.city      ?? "",
    state:     existing?.state     ?? "",
    zip:       existing?.zip       ?? "",
  });

  const set = (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => {
      const payload = {
        partner_entity_id: partnerId,
        address:           form.address.trim(),
        address_2:         form.address_2.trim() || undefined,
        city:              form.city.trim(),
        state:             form.state,
        zip:               form.zip.trim(),
      };
      return isEditing
        ? updateAddress(existing.id, payload)
        : createAddress(payload);
    },
    onSuccess,
  });

  const isValid = form.address.trim() && form.city.trim() && form.state && form.zip.trim();

  const STATES = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN",
    "IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH",
    "NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT",
    "VT","VA","WA","WV","WI","WY",
  ];

  return (
    <Modal title={isEditing ? "Edit address" : "Add address"} onClose={onClose}>
      <div className="space-y-4">
        <FormField label="Street address">
          <input className="input" value={form.address} onChange={set("address")} placeholder="123 Main St" autoFocus />
        </FormField>
        <FormField label="Address line 2">
          <input className="input" value={form.address_2} onChange={set("address_2")} placeholder="Suite 200" />
        </FormField>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="City">
            <input className="input" value={form.city} onChange={set("city")} placeholder="Portland" />
          </FormField>
          <FormField label="State">
            <Select value={form.state} onChange={set("state")}>
              <option value="" disabled>State</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
          <FormField label="ZIP">
            <input className="input" value={form.zip} onChange={set("zip")} placeholder="97201" />
          </FormField>
        </div>
        {error && <p className="text-sm text-red-500">Something went wrong. Please try again.</p>}
        <div className="flex gap-3 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!isValid || isPending}
            onClick={() => mutate()}
          >
            {isPending ? <Spinner className="h-4" /> : isEditing ? "Save changes" : "Create address"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Schedule Modal (create + edit) ───────────────────────────────────────────

type SlotState  = { start: string; end: string };
type DaySlots   = Record<DayOfWeek, SlotState[]>;

export function ScheduleModal({
  partnerId,
  addresses,
  existing,
  onClose,
  onSuccess,
}: {
  partnerId: number;
  addresses: EntityAddress[];
  existing:  Schedule | null;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const isEditing = !!existing;

  const [addressId, setAddressId] = useState(
    existing ? String(existing.entity_address_id)
             : addresses.length === 1 ? String(addresses[0].id) : ""
  );

  const initSlots = (day: DayOfWeek): SlotState[] => {
    const saved = existing?.schedule?.[day];
    if (!saved || saved.length === 0) return [];
    return saved.map((s) => ({ start: slotToInput(s.start), end: slotToInput(s.end) }));
  };

  const [slots, setSlots] = useState<DaySlots>({
    monday:    initSlots("monday"),
    tuesday:   initSlots("tuesday"),
    wednesday: initSlots("wednesday"),
    thursday:  initSlots("thursday"),
    friday:    initSlots("friday"),
    saturday:  initSlots("saturday"),
    sunday:    initSlots("sunday"),
  });

  const addSlot = (day: DayOfWeek) => {
    setSlots((prev) => ({ ...prev, [day]: [...prev[day], { start: "", end: "" }] }));
  };

  const removeSlot = (day: DayOfWeek, index: number) => {
    setSlots((prev) => ({ ...prev, [day]: prev[day].filter((_, i) => i !== index) }));
  };

  const updateSlot = (day: DayOfWeek, index: number, field: "start" | "end", value: string) => {
    setSlots((prev) => {
      const updated = prev[day].map((s, i) => (i === index ? { ...s, [field]: value } : s));
      return { ...prev, [day]: updated };
    });
  };

  // Build the schedule JSONB — only include days with complete slots. Times are
  // Pacific wall-clock "HH:MM:SS"; no zone conversion (see utils/scheduleTime).
  const buildSchedulePayload = () => {
    const payload: Record<string, Array<{ start: string; end: string }>> = {};
    for (const day of DAYS) {
      const complete = slots[day].filter((s) => s.start && s.end);
      if (complete.length > 0) {
        payload[day] = complete.map((s) => ({ start: inputToSlot(s.start), end: inputToSlot(s.end) }));
      }
    }
    return payload;
  };

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => {
      const schedule = buildSchedulePayload();
      if (isEditing) {
        return updateSchedule(existing.id, { entity_address_id: Number(addressId), schedule });
      }
      return createSchedule({ partner_entity_id: partnerId, entity_address_id: Number(addressId), schedule });
    },
    onSuccess,
  });

  // "Clear schedule" requires a double click: the first click just arms the
  // confirmation (swapping the label to "Are you sure?"); the second click,
  // while armed, fires the actual delete.
  const [confirmingClear, setConfirmingClear] = useState(false);

  const { mutate: clearSchedule, isPending: isClearing, error: clearError } = useMutation({
    mutationFn: () => deleteSchedule(existing!.id),
    onSuccess,
  });

  const handleClearClick = () => {
    if (confirmingClear) {
      clearSchedule();
    } else {
      setConfirmingClear(true);
    }
  };

  const hasAtLeastOneSlot = DAYS.some((d) => slots[d].some((s) => s.start && s.end));
  const isValid = addressId && hasAtLeastOneSlot;

  return (
    <Modal title={isEditing ? "Edit schedule" : "Add schedule"} onClose={onClose}>
      <div className="space-y-4">
        <FormField label="Address">
          <Select value={addressId} onChange={(e) => setAddressId(e.target.value)}>
            <option value="" disabled>Select an address…</option>
            {addresses.map((a) => (
              <option key={a.id} value={a.id}>
                {a.address}, {a.city}
              </option>
            ))}
          </Select>
        </FormField>

        <div>
          <label className="label mb-2">Pickup days and times ({SCHEDULE_TIME_ZONE_LABEL})</label>
          <div className="space-y-3">
            {DAYS.map((day) => (
              <div key={day}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm capitalize text-gray-600 font-medium">{day}</span>
                  <button
                    type="button"
                    className="text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
                    onClick={() => addSlot(day)}
                  >
                    + Add slot
                  </button>
                </div>
                {slots[day].length === 0 ? (
                  <p className="text-xs text-gray-400 pl-1">No pickups</p>
                ) : (
                  <div className="space-y-1.5">
                    {slots[day].map((slot, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="time"
                          className="input flex-1"
                          value={slot.start}
                          onChange={(e) => updateSlot(day, i, "start", e.target.value)}
                        />
                        <span className="text-xs text-gray-400 shrink-0">to</span>
                        <input
                          type="time"
                          className="input flex-1"
                          value={slot.end}
                          onChange={(e) => updateSlot(day, i, "end", e.target.value)}
                        />
                        <button
                          type="button"
                          className="text-gray-400 hover:text-red-500 transition-colors shrink-0"
                          onClick={() => removeSlot(day, i)}
                          title="Remove slot"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            At least one complete slot (start and end) is required.
          </p>
        </div>

        {error && <p className="text-sm text-red-500">Something went wrong. Please try again.</p>}
        {clearError && <p className="text-sm text-red-500">Couldn't clear the schedule. Please try again.</p>}
        <div className="flex items-center justify-between gap-3 pt-2">
          <div>
            {isEditing && (
              <button
                type="button"
                className={`text-xs font-medium transition-colors ${
                  confirmingClear ? "text-red-600" : "text-red-400 hover:text-red-600"
                }`}
                disabled={isClearing}
                onClick={handleClearClick}
              >
                {isClearing ? <Spinner className="h-4" /> : confirmingClear ? "Are you sure?" : "Clear schedule"}
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button
              className="btn-primary"
              disabled={!isValid || isPending}
              onClick={() => mutate()}
            >
              {isPending ? <Spinner className="h-4" /> : isEditing ? "Save changes" : "Create schedule"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Note Actions Menu ────────────────────────────────────────────────────────

function NoteActionsMenu({
  onEdit,
  onDelete,
}: {
  onEdit:   () => void;
  onDelete: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  return (
    <div ref={ref} className="relative shrink-0 ml-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100"
        title="Note actions"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-7 z-50 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
          <button
            onClick={() => { setIsOpen(false); onEdit(); }}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Edit note
          </button>
          <div className="border-t border-gray-100 my-1" />
          <button
            onClick={() => { setIsOpen(false); onDelete(); }}
            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            Remove note
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Edit Note Modal ──────────────────────────────────────────────────────────

function EditNoteModal({
  note,
  onClose,
  onSuccess,
}: {
  note:      Note;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const [text, setText] = useState(note.note);

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => updateNote(note.id, text.trim()),
    onSuccess,
  });

  return (
    <Modal title="Edit note" onClose={onClose}>
      <div className="space-y-4">
        <FormField label="Note">
          <textarea
            className="input min-h-[80px]"
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
          />
        </FormField>
        {error && <p className="text-sm text-red-500">Something went wrong.</p>}
        <div className="flex gap-3 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!text.trim() || isPending}
            onClick={() => mutate()}
          >
            {isPending ? <Spinner className="h-4" /> : "Save changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Delete Note Modal ────────────────────────────────────────────────────────

function DeleteNoteModal({
  note,
  onClose,
  onSuccess,
}: {
  note:      Note;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteNote(note.id),
    onSuccess,
  });

  return (
    <Modal title="Remove note" onClose={onClose}>
      <p className="text-sm text-gray-600 mb-4">
        Are you sure you want to remove this note? This action cannot be undone.
      </p>
      <div className="bg-gray-50 rounded-lg px-3 py-2 mb-4 text-sm text-gray-500 italic">
        &ldquo;{note.note}&rdquo;
      </div>
      <div className="flex gap-3">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary bg-red-600 hover:bg-red-700"
          disabled={isPending}
          onClick={() => mutate()}
        >
          {isPending ? <Spinner className="h-4" /> : "Remove"}
        </button>
      </div>
    </Modal>
  );
}
// ─── Remove Partner Modal ─────────────────────────────────────────────────────

function RemovePartnerModal({
  partnerId,
  partnerName,
  onClose,
  onSuccess,
}: {
  partnerId:   number;
  partnerName: string;
  onClose:     () => void;
  onSuccess:   () => void;
}) {
  const { mutate, isPending } = useMutation({
    mutationFn: () => deletePartner(partnerId),
    onSuccess,
  });

  return (
    <Modal title="Remove partner" onClose={onClose}>
      <p className="text-sm text-gray-600 mb-4">
        Are you sure you want to remove <strong>{partnerName}</strong>?
      </p>
      <div className="flex gap-3">
        <button className="btn-secondary" onClick={onClose}>No</button>
        <button
          className="btn-primary bg-red-600 hover:bg-red-700"
          disabled={isPending}
          onClick={() => mutate()}
        >
          {isPending ? <Spinner className="h-4" /> : "Yes"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Edit Name Modal ──────────────────────────────────────────────────────────

function EditPartnerModal({
  partnerId,
  currentName,
  currentType,
  currentActive,
  onClose,
  onSuccess,
}: {
  partnerId:     number;
  currentName:   string;
  currentType:   string;
  currentActive: boolean;
  onClose:       () => void;
  onSuccess:     () => void;
}) {
  const [name, setName]     = useState(currentName);
  const [type, setType]     = useState(currentType);
  const [active, setActive] = useState(currentActive);

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => updatePartner(partnerId, { name: name.trim(), type: type as any, active }),
    onSuccess,
  });

  return (
    <Modal title="Edit partner" onClose={onClose}>
      <div className="space-y-4">
        <FormField label="Partner name">
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </FormField>

        <FormField label="Type">
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="donator">Donator</option>
            <option value="distributor">Distributor</option>
            <option value="ops">Ops</option>
          </Select>
        </FormField>

        <div className="flex items-center justify-between py-1">
          <span className="text-sm text-gray-700">Partner active</span>
          <button
            type="button"
            onClick={() => setActive(!active)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
              active ? "bg-green-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                active ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {error && <p className="text-sm text-red-500">Something went wrong.</p>}
        <div className="flex gap-3 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!name.trim() || isPending}
            onClick={() => mutate()}
          >
            {isPending ? <Spinner className="h-4" /> : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Add Blackout Day Modal ───────────────────────────────────────────────────

function AddBlackoutDayModal({
  partnerId,
  existingDates,
  onClose,
  onSuccess,
}: {
  partnerId:     number;
  existingDates: string[];
  onClose:       () => void;
  onSuccess:     () => void;
}) {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState("");
  const isAlreadyBlackedOut = existingDates.includes(date);

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => createBlackoutDay({ partner_entity_id: partnerId, date }),
    onSuccess,
  });

  const isValid = !!date && !isAlreadyBlackedOut;

  return (
    <Modal title="Add blackout day" onClose={onClose}>
      <div className="space-y-4">
        <FormField
          label="Date"
          error={isAlreadyBlackedOut ? "This date is already a blackout day." : undefined}
        >
          <input
            className="input"
            type="date"
            value={date}
            min={today}
            onChange={(e) => setDate(e.target.value)}
          />
        </FormField>
        {error && <p className="text-sm text-red-500">Something went wrong. Please try again.</p>}
        <div className="flex gap-3 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!isValid || isPending}
            onClick={() => mutate()}
          >
            {isPending ? <Spinner className="h-4" /> : "Add blackout day"}
          </button>
        </div>
      </div>
    </Modal>
  );
}